
import { useState, useEffect, useRef } from 'react';

const cleaningSteps = [
  {
    icon: 'ri-drop-line',
    title: 'Günlük Temizlik',
    description: 'Her çıkardığınızda ılık su ile durulayın',
    tips: [
      'Sabah ve akşam mutlaka temizleyin',
      'Sert fırça kullanmayın',
      'Antibakteriyel gargarayla durulayın'
    ]
  },
  {
    icon: 'ri-brush-line',
    title: 'Haftalık Bakım',
    description: 'Haftada 2-3 kez özel temizlik tableti kullanın',
    tips: [
      'Temizlik tabletini 15 dakika bekletin',
      'Sonrasında iyice durulayın',
      'Yumuşak diş fırçasıyla nazikçe fırçalayın'
    ]
  }
];

export default function CleaningGuideSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="cleaning-guide"
      className="py-20 section-padding bg-black/30"
    >
      <div className="container-max">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'fade-in' : 'opacity-0'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gold-text">Temizlik Rehberi</span>
          </h2>
          <div className="w-24 h-1 gold-gradient mx-auto rounded-full"></div>
          <p className="text-xl text-gray-300 mt-6 max-w-2xl mx-auto">
            Şeffaf plaklarınızın uzun ömürlü olması için doğru temizlik
            yöntemleri
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {cleaningSteps.map((step, index) => (
            <div
              key={index}
              className={`card-premium p-8 text-center group hover:scale-105 transition-all duration-700 ${
                isVisible ? 'slide-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gold-light to-gold-dark rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <i className={`${step.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold gold-text mb-4">{step.title}</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {step.description}
              </p>

              <div className="space-y-3">
                {step.tips.map((tip, tipIndex) => (
                  <div key={tipIndex} className="flex items-start text-left">
                    <i className="ri-check-line text-gold-text mt-1 mr-3 flex-shrink-0"></i>
                    <span className="text-gray-400 text-sm">{tip}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Önemli Notlar */}
        <div
          className={`mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 ${
            isVisible ? 'fade-in' : 'opacity-0'
          }`}
        >
          <div className="card-premium p-8 border-l-4 border-red-500">
            <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center">
              <i className="ri-error-warning-line text-white mr-3"></i>
              Yapılmaması Gerekenler
            </h3>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <i className="ri-close-line text-red-400 mt-1 mr-3 flex-shrink-0"></i>
                <span>Sıcak su kullanmayın (deformasyona neden olur)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-line text-red-400 mt-1 mr-3 flex-shrink-0"></i>
                <span>Diş macunu ile fırçalamayın (çizik yapar)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-line text-red-400 mt-1 mr-3 flex-shrink-0"></i>
                <span>Ağızlık suyu ile bekletmeyin (renk değişimi)</span>
              </li>
            </ul>
          </div>

          <div className="card-premium p-8 border-l-4 border-green-500">
            <h3 className="text-xl font-bold text-green-400 mb-4 flex items-center">
              <i className="ri-lightbulb-line text-white mr-3"></i>
              Pro İpuçları
            </h3>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <i className="ri-check-line text-green-400 mt-1 mr-3 flex-shrink-0"></i>
                <span>Her zaman temiz ellerde tutun</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-400 mt-1 mr-3 flex-shrink-0"></i>
                <span>Özel saklama kutusunda muhafaza edin</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-green-400 mt-1 mr-3 flex-shrink-0"></i>
                <span>Düzenli diş hekimi kontrolü yaptırın</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
